/**
 * Model Selection Store
 *
 * Adapted from Loft's modelStore — fetches the live model catalogue from
 * the Anthropic API (via the platform admin proxy) with a 24-hour cache,
 * falling back to a hardcoded list.
 *
 * Feeds model selectors in the playground and any future agent-creation flows.
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { api } from '@/lib/api'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ModelOption {
  /** Model ID (e.g. "claude-sonnet-4-20250514") */
  value: string
  /** Display name (e.g. "Claude Sonnet 4") */
  label: string
}

interface ModelState {
  /** Available model options for pickers. */
  models: ModelOption[]
  /** Unix ms timestamp of last successful fetch, or null. */
  lastFetched: number | null
  /** True while a fetch is in flight. */
  isLoading: boolean
  /** Last fetch error message, or null. */
  error: string | null
  /** Fetch models, respecting the 24h cache TTL. */
  fetchModels: () => Promise<void>
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CACHE_TTL_MS = 24 * 60 * 60 * 1000 // 24 hours

/**
 * Fallback model list used when the API is unavailable.
 * Updated to latest known model generations as of May 2026.
 */
const FALLBACK_MODELS: ModelOption[] = [
  { value: 'claude-opus-4-7', label: 'Claude Opus 4.7' },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
  { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' },
  { value: 'claude-opus-4-6', label: 'Claude Opus 4.6 (legacy)' },
  { value: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5 (legacy)' },
  { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4 (2025-05-14)' },
]

// ── Store ─────────────────────────────────────────────────────────────────────

export const useModelStore = create<ModelState>()(
  persist(
    (set, get) => ({
      models: FALLBACK_MODELS,
      lastFetched: null,
      isLoading: false,
      error: null,

      async fetchModels() {
        const { lastFetched, isLoading } = get()

        // Skip if another fetch is in flight
        if (isLoading) return

        // Skip if cache is still fresh (24h TTL)
        if (lastFetched !== null && Date.now() - lastFetched < CACHE_TTL_MS) {
          return
        }

        set({ isLoading: true, error: null })

        try {
          // Try to fetch from the platform admin API
          const response = await api.get<{ models: ModelOption[] }>('/v1/admin/models')

          if (response.models && response.models.length > 0) {
            set({
              models: response.models,
              lastFetched: Date.now(),
              isLoading: false,
            })
          } else {
            // Empty response — keep fallback, mark as fetched
            set({ isLoading: false, lastFetched: Date.now() })
          }
        } catch {
          // API unavailable — keep existing models (fallback or cached)
          set({ isLoading: false, error: 'Failed to fetch models' })
        }
      },
    }),
    {
      name: 'stream-models',
      storage: createJSONStorage(() => localStorage),
      // Only persist data, not transient loading/error state
      partialize: (state) => ({
        models: state.models,
        lastFetched: state.lastFetched,
      }),
    }
  )
)
